import React, { useState, useRef, useEffect } from 'react';
import '../../styles/Auth.css';
import apiService from '../../apiService';
import MapPickerModal from '../MapPickerModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ── OTP boxes ─────────────────────────────────────────────────────────────────
const OtpBoxes = ({ value, onChange }) => {
  const digits = (value + '      ').slice(0, 6).split('');
  const r0 = useRef(null), r1 = useRef(null), r2 = useRef(null);
  const r3 = useRef(null), r4 = useRef(null), r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];

  const handle = (i, e) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.slice(0, i) + (ch || ' ') + value.slice(i + 1);
    onChange(next.trimEnd());
    if (ch && i < 5) refs[i + 1].current?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i].trim() && i > 0) {
      refs[i - 1].current?.focus();
      onChange((value.slice(0, i - 1) + ' ' + value.slice(i)).trimEnd());
    }
  };
  return (
    <div className="otp-container">
      {digits.map((d, i) => (
        <input key={i} ref={refs[i]}
          type="tel" inputMode="numeric" maxLength={1}
          value={d.trim()}
          onChange={e => handle(i, e)}
          onKeyDown={e => handleKey(i, e)}
          className={`otp-input${d.trim() ? ' filled' : ''}`}
        />
      ))}
    </div>
  );
};

// ── Preference options ────────────────────────────────────────────────────────
const PREF_OPTIONS = [
  { value: 'mobile', icon: '📱', label: 'Mobile OTP',       desc: 'Quick OTP login' },
  { value: 'email',  icon: '✉️',  label: 'Email & Password', desc: 'Classic login'   },
  { value: 'both',   icon: '🔒', label: 'Both',             desc: 'Most flexible'   },
];

// ── Main Component ────────────────────────────────────────────────────────────
const SignUpModal = ({
  isOpen, onClose, onBackToLogin, authStep, setAuthStep,
  isAuthenticated, setIsAuthenticated, socialMediaLoginEnabled,
  onShowAbout, onRegistrationSuccess,
}) => {
  // ── Form state ───────────────────────────────────────────────────────────
  const [loginPreference, setLoginPreference] = useState('both');
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '',
    password: '', password_confirmation: '', terms: false,
  });
  const [whatsappEnabled, setWhatsappEnabled]     = useState(false);
  const [whatsappNumber, setWhatsappNumber]       = useState('');
  const [useCustomWhatsapp, setUseCustomWhatsapp] = useState(false);
  const [address, setAddress] = useState({
    address: '', landmark: '', city: '', state: '',
    pincode: '', country: 'India', latitude: '', longitude: '',
  });
  const [showMapPicker, setShowMapPicker]       = useState(false);
  const [addressExpanded, setAddressExpanded]   = useState(true);
  const [errors, setErrors]                     = useState({});
  const [touched, setTouched]                   = useState({});
  const [isLoading, setIsLoading]               = useState(false);
  const [showPassword, setShowPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [successMessage, setSuccessMessage]     = useState('');
  const [mobileAlreadyExists, setMobileAlreadyExists] = useState(false);

  // ── OTP / step state ─────────────────────────────────────────────────────
  const [signupStep, setSignupStep]   = useState('form'); // 'form' | 'verify'
  const [mobileOtp, setMobileOtp]     = useState('');
  const [emailOtp, setEmailOtp]       = useState('');
  const [sendingOtp, setSendingOtp]   = useState(false);
  const [otpError, setOtpError]       = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // ── Derived flags ─────────────────────────────────────────────────────────
  // Mobile is required & OTP-verified for every preference. Email is required &
  // OTP-verified only when the login method involves email.
  const needsMobile = true;
  const needsEmail  = loginPreference === 'email' || loginPreference === 'both';

  // ── Password helpers ──────────────────────────────────────────────────────
  const calcStrength = (p) => {
    let s = 0;
    if (p.length >= 8) s++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^a-zA-Z0-9]/.test(p)) s++;
    return s;
  };
  const strengthLabel = ['', 'Weak password', 'Fair password', 'Good password', 'Strong password'][passwordStrength] || '';
  const strengthClass = passwordStrength <= 1 ? 'weak' : passwordStrength === 2 ? 'medium' : 'strong';

  // ── Validation ────────────────────────────────────────────────────────────
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Please enter your full name';
        if (value.length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(value)) return 'Name can only contain letters, spaces, hyphens and apostrophes';
        return '';
      case 'mobile':
        if (!needsMobile) return '';
        if (!value.trim()) return 'Please enter your mobile number';
        if (!/^\+?[1-9]\d{7,14}$/.test(value.replace(/\s/g, ''))) return 'Please enter a valid mobile number';
        return '';
      case 'email':
        if (!needsEmail) {
          // Optional in mobile-only mode, but if provided must be valid
          if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
          return '';
        }
        if (!value.trim()) return 'Please enter your email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!needsEmail) return '';
        if (!value) return 'Please enter a password';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])(?=.*[A-Z])/.test(value)) return 'Password must contain uppercase and lowercase letters';
        if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
        return '';
      case 'password_confirmation':
        if (!needsEmail) return '';
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';
      case 'terms':
        return value ? '' : 'You must agree to the terms and conditions';
      default:
        return '';
    }
  };

  const validateAll = () => {
    const fields = ['name', ...(needsMobile ? ['mobile'] : []), 'email',
                    ...(needsEmail ? ['password', 'password_confirmation'] : []), 'terms'];
    const newErrors = {};
    fields.forEach(k => {
      const err = validateField(k, k === 'terms' ? formData.terms : formData[k]);
      if (err) newErrors[k] = err;
    });

    // Delivery address is required for all preferences
    if (!address.address?.trim()) newErrors.addr_address = 'Please enter your delivery address';
    if (!address.city?.trim())    newErrors.addr_city    = 'Please enter your city';
    if (!address.pincode || String(address.pincode).replace(/\D/g, '').length !== 6)
      newErrors.addr_pincode = 'Enter a valid 6-digit pincode';

    setErrors(newErrors);
    setTouched(fields.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    if (Object.keys(newErrors).some(k => k.startsWith('addr_'))) setAddressExpanded(true);
    return !Object.keys(newErrors).length;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setFormData(p => ({ ...p, [name]: v }));
    if (name === 'mobile') { setMobileAlreadyExists(false); if (!useCustomWhatsapp) setWhatsappNumber(v); }
    if (name === 'password') setPasswordStrength(calcStrength(value));
    if (touched[name]) setErrors(p => ({ ...p, [name]: validateField(name, v) }));
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setTouched(p => ({ ...p, [name]: true }));
    setErrors(p => ({ ...p, [name]: validateField(name, v) }));
  };

  // ── Step 1: validate form → send OTP(s) → unified verify screen ──────────
  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    if (!validateAll()) return;

    setSendingOtp(true); setOtpError('');
    setMobileOtp(''); setEmailOtp('');
    try {
      // Mobile OTP is always sent (mobile is required for every preference)
      const mobRes = await apiService.request('/signup/verify-mobile/send', {
        method: 'POST', body: JSON.stringify({ mobile: formData.mobile }), auth: false,
      });
      if (!(mobRes.success || mobRes.status === 200)) {
        setOtpError(mobRes.data?.message || 'Could not send OTP to mobile. Please try again.');
        return;
      }

      // Email OTP only when email is part of the login method
      if (needsEmail) {
        const emRes = await apiService.request('/signup/verify-email/send', {
          method: 'POST', body: JSON.stringify({ email: formData.email }), auth: false,
        });
        if (!(emRes.success || emRes.status === 200)) {
          setOtpError(emRes.data?.message || 'Could not send OTP to email. Please try again.');
          return;
        }
      }

      setSignupStep('verify');
      setResendTimer(30);
    } catch { setOtpError('Network error. Please check your connection.'); }
    finally { setSendingOtp(false); }
  };

  // ── Step 2: submit OTP(s) → backend verifies & creates account ───────────
  const handleVerifyAndCreate = async () => {
    const mCode = mobileOtp.replace(/\s/g, '');
    if (mCode.length < 6) { setOtpError('Enter the 6-digit mobile code.'); return; }
    const eCode = emailOtp.replace(/\s/g, '');
    if (needsEmail && eCode.length < 6) { setOtpError('Enter the 6-digit email code.'); return; }
    await doRegister(mCode, needsEmail ? eCode : null);
  };

  // ── Final: create account (OTP-gated in backend) ─────────────────────────
  const doRegister = async (mobileCode, emailCode) => {
    setIsLoading(true); setOtpError('');
    try {
      const payload = {
        ...formData,
        login_preference: loginPreference,
        mobile_otp: mobileCode,
        ...(emailCode ? { email_otp: emailCode } : {}),
        whatsapp_enabled: whatsappEnabled,
        whatsapp_number: whatsappEnabled
          ? (useCustomWhatsapp && whatsappNumber ? whatsappNumber : formData.mobile)
          : null,
        delivery_address: {
          address: address.address, landmark: address.landmark,
          city: address.city, state: address.state, pincode: address.pincode,
          country: address.country || 'India',
          latitude: address.latitude || null, longitude: address.longitude || null,
          recipient_name: formData.name, phone: formData.mobile, label: 'Home',
        },
      };

      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Account created successfully! Redirecting…');
        if (data.token) apiService.setToken(data.token);
        if (data.user)  apiService.setUser(data.user);
        if (onRegistrationSuccess) onRegistrationSuccess(data.user);
        else setTimeout(() => { window.location.href = '/'; }, 1500);
      } else {
        const errs = data.errors || {};
        // OTP errors keep the user on the verify screen
        const otpErr = (Array.isArray(errs.mobile_otp) ? errs.mobile_otp[0] : errs.mobile_otp)
          || (Array.isArray(errs.email_otp) ? errs.email_otp[0] : errs.email_otp);
        if (otpErr) {
          setOtpError(otpErr);
        } else if (Object.keys(errs).length) {
          setErrors(errs);
          const mErr = Array.isArray(errs.mobile) ? errs.mobile[0] : (errs.mobile || '');
          if (mErr.toLowerCase().includes('already')) setMobileAlreadyExists(true);
          setSignupStep('form');
        } else {
          setErrors({ general: data.message || 'Registration failed.' });
          setSignupStep('form');
        }
      }
    } catch { setOtpError('An error occurred. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setSendingOtp(true); setOtpError('');
    try {
      await apiService.request('/signup/verify-mobile/send', {
        method: 'POST', body: JSON.stringify({ mobile: formData.mobile }), auth: false,
      });
      setMobileOtp('');
      if (needsEmail) {
        await apiService.request('/signup/verify-email/send', {
          method: 'POST', body: JSON.stringify({ email: formData.email }), auth: false,
        });
        setEmailOtp('');
      }
      setResendTimer(30);
    } finally { setSendingOtp(false); }
  };

  const handleMapConfirm = (data) => {
    setAddress(p => ({
      ...p, address: data.address || p.address, city: data.city || p.city,
      state: data.state || p.state, pincode: data.pincode || p.pincode,
      country: data.country || p.country || 'India',
      latitude: data.latitude || p.latitude, longitude: data.longitude || p.longitude,
    }));
    setShowMapPicker(false);
    if (!addressExpanded) setAddressExpanded(true);
  };

  if (!isOpen) return null;

  // ── Unified OTP verification screen ────────────────────────────────────────
  if (signupStep === 'verify') {
    const maskMobile = (m) => (m ? m.replace(/\d(?=\d{2})/g, '•').slice(-12) : '');
    const maskEmail  = (e) => {
      if (!e) return '';
      const [u, d] = e.split('@');
      return `${u.slice(0, 2)}${'•'.repeat(Math.max(1, u.length - 2))}@${d}`;
    };

    return (
      <div className="modal-overlay" style={{ zIndex: 300, alignItems: 'flex-start', padding: '20px 0' }}>
        <div className="signup-container">
          <div className="signup-card">
            <div className="signup-header">
              <div className="logo" style={{ fontSize: 28 }}>🔐</div>
              <h2>Verify Your {needsEmail ? 'Details' : 'Mobile'}</h2>
              <p className="subtitle">
                Enter the {needsEmail ? 'codes' : 'code'} we sent to confirm your account
              </p>
            </div>

            {successMessage
              ? <div className="success-message"><span>✅</span><span>{successMessage}</span></div>
              : <>
                  {/* Mobile OTP — always */}
                  <div className="form-group">
                    <label>📱 Mobile OTP <span style={{ fontWeight: 400, color: '#6b7280' }}>— sent to {maskMobile(formData.mobile)}</span></label>
                    <OtpBoxes value={mobileOtp} onChange={setMobileOtp} />
                  </div>

                  {/* Email OTP — only for email/both */}
                  {needsEmail && (
                    <div className="form-group">
                      <label>✉️ Email OTP <span style={{ fontWeight: 400, color: '#6b7280' }}>— sent to {maskEmail(formData.email)}</span></label>
                      <OtpBoxes value={emailOtp} onChange={setEmailOtp} />
                    </div>
                  )}

                  {otpError && (
                    <div className="field-error" style={{ justifyContent: 'center', marginTop: 4 }}>
                      <span>⚠️</span><span>{otpError}</span>
                    </div>
                  )}

                  <div className="otp-timer">
                    {resendTimer > 0
                      ? `Resend ${needsEmail ? 'codes' : 'code'} in ${resendTimer}s`
                      : <button className="resend-btn" onClick={handleResend} disabled={sendingOtp}>
                          {sendingOtp ? 'Sending…' : `Resend ${needsEmail ? 'codes' : 'code'}`}
                        </button>}
                  </div>

                  <button
                    className="submit-btn"
                    onClick={handleVerifyAndCreate}
                    disabled={isLoading || sendingOtp}
                    style={{ marginTop: 20 }}
                  >
                    {isLoading
                      ? <><span className="loading-spinner" /> Creating account…</>
                      : '✓ Verify & Create Account'}
                  </button>

                  <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <button className="resend-btn"
                      onClick={() => { setSignupStep('form'); setOtpError(''); }}>
                      ← Back to edit details
                    </button>
                  </div>
                </>
            }
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ─────────────────────────────────────────────────────
  const handleGoogleLogin   = () => { window.location.href = `${API_URL}/auth/google`; };
  const handleFacebookLogin = () => { window.location.href = `${API_URL}/auth/facebook`; };

  return (
    <div className="modal-overlay" style={{ zIndex: 300, alignItems: 'flex-start', padding: '20px 0' }}>
      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <div className="logo">A</div>
            <h1>Create Account</h1>
            <p className="subtitle">Join thousands of users already on board</p>
          </div>

          {successMessage && <div className="success-message"><span>✅</span><span>{successMessage}</span></div>}
          {errors.general  && <div className="error-message-general"><span>⚠️</span><span>{errors.general}</span></div>}

          <form onSubmit={handleFormSubmit} noValidate>

            {/* ── 1. Login Preference (TOP) ── */}
            <div className="form-group">
              <label>How would you like to sign in?</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 4 }}>
                {PREF_OPTIONS.map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => { setLoginPreference(opt.value); setErrors({}); setTouched({}); }}
                    style={{
                      padding: '12px 6px', textAlign: 'center', cursor: 'pointer',
                      border: `2px solid ${loginPreference === opt.value ? '#667eea' : '#e5e7eb'}`,
                      borderRadius: 10, background: loginPreference === opt.value ? '#eef2ff' : '#fff',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ fontSize: 22 }}>{opt.icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginTop: 4 }}>{opt.label}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                {loginPreference === 'mobile' && "You'll receive an OTP on your mobile to sign in each time."}
                {loginPreference === 'email'  && "You'll sign in using your email address and password."}
                {loginPreference === 'both'   && "You can sign in with either mobile OTP or email & password."}
              </p>
            </div>

            {/* ── 2. Full Name ── */}
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-wrapper">
                <input type="text" id="name" name="name" value={formData.name}
                  onChange={handleChange} onBlur={handleBlur}
                  placeholder="Your full name" autoComplete="name"
                  className={errors.name && touched.name ? 'error' : (touched.name && !errors.name ? 'success' : '')}
                />
              </div>
              {errors.name && touched.name && <div className="field-error"><span>⚠️</span><span>{errors.name}</span></div>}
            </div>

            {/* ── 3. Mobile (only for mobile/both) ── */}
            {needsMobile && (
              <div className="form-group">
                <label htmlFor="mobile">Mobile Number</label>
                <div className="input-wrapper">
                  <input type="text" id="mobile" name="mobile" value={formData.mobile}
                    onChange={handleChange} onBlur={handleBlur}
                    placeholder="+91 9XXXXXXXXX" autoComplete="tel"
                    className={errors.mobile && touched.mobile ? 'error' : (touched.mobile && !errors.mobile ? 'success' : '')}
                  />
                </div>
                {errors.mobile && touched.mobile && !mobileAlreadyExists && (
                  <div className="field-error"><span>⚠️</span><span>{errors.mobile}</span></div>
                )}
                {mobileAlreadyExists && (
                  <div className="rounded-2xl overflow-hidden border border-orange-200 mt-2">
                    <div className="flex items-start gap-2.5 px-4 py-3 bg-orange-50">
                      <span className="text-orange-500 mt-0.5">⚠️</span>
                      <span className="text-sm font-medium text-orange-700">This mobile number is already registered.</span>
                    </div>
                    <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-white text-xs font-bold">Already have an account?</p>
                        <p className="text-white/75 text-[11px]">Sign in with this number instead</p>
                      </div>
                      <button type="button" onClick={onBackToLogin}
                        className="flex-shrink-0 bg-white text-orange-600 text-xs font-bold px-4 py-2 rounded-xl shadow-sm">
                        Sign In →
                      </button>
                    </div>
                  </div>
                )}

                {/* WhatsApp opt-in */}
                <div style={{
                  marginTop: 12, background: whatsappEnabled ? '#f0fdf4' : '#f9f9f9',
                  border: `1.5px solid ${whatsappEnabled ? '#86efac' : '#e8e8e8'}`,
                  borderRadius: 14, padding: '12px 14px', transition: 'all 0.2s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>💬</span>
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#1b1b1b' }}>WhatsApp Updates</p>
                        <p style={{ margin: 0, fontSize: 11, color: '#666', marginTop: 1 }}>Get order updates &amp; offers</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => setWhatsappEnabled(v => !v)}
                      style={{
                        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                        background: whatsappEnabled ? '#25d366' : '#d1d5db',
                        position: 'relative', flexShrink: 0, transition: 'background 0.2s',
                      }}>
                      <span style={{
                        position: 'absolute', top: 2, left: whatsappEnabled ? 22 : 2,
                        width: 20, height: 20, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)', transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>
                  {whatsappEnabled && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #bbf7d0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', cursor: 'pointer' }}>
                        <input type="checkbox" checked={useCustomWhatsapp}
                          onChange={e => { setUseCustomWhatsapp(e.target.checked); if (!e.target.checked) setWhatsappNumber(formData.mobile); }}
                          style={{ accentColor: '#25d366', width: 14, height: 14 }}
                        />
                        Use a different WhatsApp number
                      </label>
                      {useCustomWhatsapp && (
                        <div className="input-wrapper" style={{ marginTop: 8 }}>
                          <input type="text" value={whatsappNumber}
                            onChange={e => setWhatsappNumber(e.target.value)}
                            placeholder="+91 WhatsApp number" style={{ background: '#fff' }}
                          />
                        </div>
                      )}
                      {!useCustomWhatsapp && formData.mobile && (
                        <p style={{ fontSize: 11, color: '#16a34a', marginTop: 6 }}>✓ Updates will be sent to {formData.mobile}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── 4. Email (always shown; optional in mobile-only mode) ── */}
            <div className="form-group">
              <label htmlFor="email">
                Email Address {!needsEmail && <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>}
              </label>
              <div className="input-wrapper">
                <input type="email" id="email" name="email" value={formData.email}
                  onChange={handleChange} onBlur={handleBlur}
                  placeholder="your@email.com" autoComplete="email"
                  className={errors.email && touched.email ? 'error' : (touched.email && formData.email && !errors.email ? 'success' : '')}
                />
              </div>
              {errors.email && touched.email && <div className="field-error"><span>⚠️</span><span>{errors.email}</span></div>}
            </div>

            {/* ── 5. Password (only for email/both) ── */}
            {needsEmail && (<>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange} onBlur={handleBlur}
                    placeholder="Create a strong password" autoComplete="new-password"
                    className={errors.password && touched.password ? 'error' : (touched.password && !errors.password ? 'success' : '')}
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? '👁️‍🗨️' : '👁️'}
                  </button>
                </div>
                {errors.password && touched.password && <div className="field-error"><span>⚠️</span><span>{errors.password}</span></div>}
                {formData.password && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      {[1,2,3,4].map(l => <div key={l} className={`strength-bar${l <= passwordStrength ? ` active ${strengthClass}` : ''}`} />)}
                    </div>
                    <div className="strength-text">{strengthLabel}</div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password_confirmation">Confirm Password</label>
                <div className="input-wrapper">
                  <input type={showConfirmPassword ? 'text' : 'password'} id="password_confirmation"
                    name="password_confirmation" value={formData.password_confirmation}
                    onChange={handleChange} onBlur={handleBlur}
                    placeholder="Re-enter your password" autoComplete="new-password"
                    className={errors.password_confirmation && touched.password_confirmation ? 'error' : (touched.password_confirmation && !errors.password_confirmation ? 'success' : '')}
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(v => !v)}>
                    {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
                  </button>
                </div>
                {errors.password_confirmation && touched.password_confirmation && (
                  <div className="field-error"><span>⚠️</span><span>{errors.password_confirmation}</span></div>
                )}
              </div>
            </>)}

            {/* ── 6. Delivery Address (required) ── */}
            <div className="form-group">
              <button type="button" onClick={() => setAddressExpanded(v => !v)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: addressExpanded ? '#fff7ed' : '#f9f9f9',
                  border: `1.5px solid ${(errors.addr_address || errors.addr_city || errors.addr_pincode) ? '#ef4444' : (addressExpanded ? '#fdba74' : '#e8e8e8')}`,
                  borderRadius: 14, padding: '11px 14px', cursor: 'pointer', transition: 'all 0.2s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>📍</span>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#1b1b1b' }}>
                      Delivery Address <span style={{ color: '#ef4444' }}>*</span>
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: '#666', marginTop: 1 }}>
                      {address.address ? `${address.address.slice(0,32)}${address.address.length > 32 ? '…' : ''}` : 'Add your home / delivery address'}
                    </p>
                  </div>
                </div>
                <span style={{ fontSize: 16, color: '#f05a23', transform: addressExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
              </button>

              {addressExpanded && (
                <div style={{ border: '1.5px solid #fdba74', borderTop: 'none', borderRadius: '0 0 14px 14px', padding: '14px 14px 10px', background: '#fffbf7' }}>
                  <button type="button" onClick={() => setShowMapPicker(true)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(90deg,#f05a23,#f7821a)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>
                    <span>🗺️</span>{address.latitude ? 'Change Location on Map' : 'Pick from Google Map'}
                  </button>
                  {address.latitude && address.longitude && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '5px 10px', marginBottom: 10, fontSize: 11, color: '#16a34a' }}>
                      <span>📌</span>Location pinned: {parseFloat(address.latitude).toFixed(5)}, {parseFloat(address.longitude).toFixed(5)}
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>Full Address <span style={{ color: '#ef4444' }}>*</span></label>
                    <textarea name="address" value={address.address}
                      onChange={e => { setAddress(p => ({ ...p, address: e.target.value })); setErrors(p => ({ ...p, addr_address: '' })); }}
                      placeholder="House / Flat no., Street, Area" rows={2}
                      style={{ width: '100%', borderRadius: 10, border: `1.5px solid ${errors.addr_address ? '#ef4444' : '#e8e8e8'}`, padding: '9px 12px', fontSize: 13, resize: 'vertical', background: '#fff', color: '#1b1b1b', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    {errors.addr_address && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.addr_address}</p>}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>Landmark <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
                    <input type="text" name="landmark" value={address.landmark}
                      onChange={e => setAddress(p => ({ ...p, landmark: e.target.value }))}
                      placeholder="Near school, temple, metro…"
                      style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e8e8e8', padding: '9px 12px', fontSize: 13, background: '#fff', color: '#1b1b1b', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>City <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="city" value={address.city}
                        onChange={e => { setAddress(p => ({ ...p, city: e.target.value })); setErrors(p => ({ ...p, addr_city: '' })); }}
                        placeholder="City"
                        style={{ width: '100%', borderRadius: 10, border: `1.5px solid ${errors.addr_city ? '#ef4444' : '#e8e8e8'}`, padding: '9px 12px', fontSize: 13, background: '#fff', color: '#1b1b1b', boxSizing: 'border-box' }} />
                      {errors.addr_city && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.addr_city}</p>}
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>Pincode <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="pincode" value={address.pincode}
                        onChange={e => { setAddress(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '') })); setErrors(p => ({ ...p, addr_pincode: '' })); }}
                        placeholder="6-digit PIN" maxLength={6}
                        style={{ width: '100%', borderRadius: 10, border: `1.5px solid ${errors.addr_pincode ? '#ef4444' : '#e8e8e8'}`, padding: '9px 12px', fontSize: 13, background: '#fff', color: '#1b1b1b', boxSizing: 'border-box' }} />
                      {errors.addr_pincode && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.addr_pincode}</p>}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>State</label>
                    <input type="text" name="state" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))} placeholder="State" style={{ width: '100%', borderRadius: 10, border: '1.5px solid #e8e8e8', padding: '9px 12px', fontSize: 13, background: '#fff', color: '#1b1b1b', boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}
            </div>

            {/* ── 6. Terms ── */}
            <div className="form-group">
              <div className="checkbox-group">
                <input type="checkbox" id="terms" name="terms" checked={formData.terms} onChange={handleChange} onBlur={handleBlur} />
                <label htmlFor="terms" className="checkbox-label">
                  I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
                </label>
              </div>
              {errors.terms && touched.terms && <div className="field-error"><span>⚠️</span><span>{errors.terms}</span></div>}
            </div>

            {otpError && <div className="error-message-general"><span>⚠️</span><span>{otpError}</span></div>}

            {/* ── 7. Submit ── */}
            <button type="submit" className="submit-btn" disabled={isLoading || sendingOtp || !formData.terms}>
              {sendingOtp
                ? <><span className="loading-spinner" />Sending verification…</>
                : needsEmail
                  ? '✉️📱 Continue to Verification →'
                  : '📱 Continue to Mobile Verification →'
              }
            </button>
          </form>

          <div className="divider"><span>Or continue with</span></div>
          {socialMediaLoginEnabled && (
            <div className="social-login">
              <button type="button" className="social-btn" onClick={handleGoogleLogin}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Google
              </button>
              <button type="button" className="social-btn facebook-btn" onClick={handleFacebookLogin}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
            </div>
          )}

          <div className="footer">
            Already have an account?{' '}
            <button type="button" onClick={onBackToLogin} className="link-button">Sign in</button>
          </div>
          {onShowAbout && (
            <p className="text-xs text-center text-gray-400 mt-3">
              <button onClick={onShowAbout} className="text-blue-500 hover:underline">About Us</button>
            </p>
          )}
        </div>
      </div>

      {showMapPicker && (
        <MapPickerModal
          onClose={() => setShowMapPicker(false)}
          onConfirm={handleMapConfirm}
          initialLat={address.latitude ? parseFloat(address.latitude) : undefined}
          initialLng={address.longitude ? parseFloat(address.longitude) : undefined}
        />
      )}
    </div>
  );
};

export default SignUpModal;
